import axios from 'axios';
import { useMemo } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import firebase from '@/shared/services/firebase';

import logger from '../utils/logger';

const auth = firebase.auth;

export const createAuthenticatedAxios = (user) => {
    // create axios instance
    const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10),
        withCredentials: true, // matches CORS with credentials
    });

    // request axios interceptor
    // Use auth.currentUser (real Firebase User) instead of the `user` state
    // variable, which may be a plain spread object from reloadCurrentUser
    // and lacks prototype methods like getIdToken.
    axiosInstance.interceptors.request.use(
        async (config) => {
            try {
                if (user && auth.currentUser) {
                    const token = await auth.currentUser.getIdToken();
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } catch (error) {
                logger.error('Error retrieving Firebase token: ', error);
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // response interceptor for token refresh and error handling
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // handle 401 errors with automatic token refresh
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                try {
                    if (user && auth.currentUser) {
                        const newToken =
                            await auth.currentUser.getIdToken(true);
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axiosInstance(originalRequest);
                    }
                } catch (refreshError) {
                    logger.error('Token refresh failed: ', refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    return axiosInstance;
};

// hook for returning authenticated axios instance
export const useAuthenticatedAxios = () => {
    const { user } = useAuth();
    return useMemo(() => {
        return createAuthenticatedAxios(user);
    }, [user]);
};

export default createAuthenticatedAxios;
